import { nearestZip3Prefixes, nearestZipFromCoords, milesBetween } from './zipDistance.js'

/**
 * zip5Locate — s101 Phase 4 (Dr. Rami 2026-10-13; TestFlight b65 x_24: "current location" put
 * Alex, on N Beachwood Dr in Hollywood, in 90604 Whittier).
 *
 * ★ THE DEFECT. "Use my current location" sent only "<ZIP3>00" (PROD-655: the device fix never
 *   leaves the phone), and the API resolved that to the middle of the whole 900 area. Every search
 *   then started miles from the patient.
 * ★ THE RULING: the phone works out its own nearest 5-DIGIT ZIP and sends only that. It asks the
 *   API for the ZIP centroids of the three-digit areas around it (up to 12) (GET /providers/zip-areas —
 *   the server learns only those areas) and picks the nearest ZIP itself. The GPS point stays on
 *   the device.
 * Never throws: any failure answers the old ZIP3 result, so a search still has somewhere to start.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {(prefixes: string[]) => Promise<{zips: {zip5:string,lat:number,lng:number}[]}>} fetchAreas
 * @returns {Promise<{ zip: string, precise: boolean }|null>}
 */
export async function nearestZip5(lat, lng, fetchAreas) {
  const coarse = nearestZipFromCoords(lat, lng)
  const fallback = coarse ? { zip: coarse.zip, precise: false } : null
  const prefixes = nearestZip3Prefixes(lat, lng)
  if (!prefixes.length || typeof fetchAreas !== 'function') return fallback
  try {
    const r = await fetchAreas(prefixes)
    const zips = Array.isArray(r && r.zips) ? r.zips : []
    let best = null
    for (const z of zips) {
      if (!z || !/^\d{5}$/.test(String(z.zip5)) || typeof z.lat !== 'number' || typeof z.lng !== 'number') continue
      const d = milesBetween(lat, lng, z.lat, z.lng)
      if (!best || d < best.d) best = { zip: String(z.zip5), d }
    }
    return best ? { zip: best.zip, precise: true } : fallback
  } catch (e) {
    return fallback
  }
}

/**
 * zip5Point — s111 (TestFlight b75, Dr. Rami: "Map needs to open centered on where the user is or zip code of record,
 * I have to scroll my way to the correct area"). The Doctors tab opened at the patient's ZIP3 centre (getZipCoords:
 * 900 is the middle of the whole LA area, miles from 90068). This is the ZIP's OWN centre from the same
 * GET /providers/zip-areas the share step uses (the server learns only the 3-digit area). Never throws; null when the
 * ZIP is not a ZCTA or the read fails (the caller keeps the coarse point).
 * @returns {Promise<{lat:number,lng:number}|null>}
 */
export async function zip5Point(zip, fetchAreas) {
  const z = String(zip || '').replace(/\D/g, '').slice(0, 5)
  if (z.length !== 5 || typeof fetchAreas !== 'function') return null
  try {
    const r = await fetchAreas([z.slice(0, 3)])
    const hit = (Array.isArray(r && r.zips) ? r.zips : []).find((x) => x && String(x.zip5) === z
      && typeof x.lat === 'number' && typeof x.lng === 'number')
    return hit ? { lat: hit.lat, lng: hit.lng } : null
  } catch (e) {
    return null
  }
}

/**
 * devicePointIfAllowed — s111: the device's position ONLY when the patient already allowed location (never a new
 * permission prompt on opening a tab). Where the browser cannot say (no Permissions API), null.
 * @returns {Promise<{lat:number,lng:number}|null>}
 */
export async function devicePointIfAllowed(nav = (typeof navigator !== 'undefined' ? navigator : null)) {
  try {
    if (!nav || !nav.geolocation || !nav.permissions || typeof nav.permissions.query !== 'function') return null
    const st = await nav.permissions.query({ name: 'geolocation' })
    if (!st || st.state !== 'granted') return null
    return await new Promise((resolve) => nav.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }))
  } catch (e) {
    return null
  }
}
